import * as React from 'react'
import cn from 'classnames'
import querystring from 'querystring'
import { FormattedMessage, defineMessages } from 'react-intl'
import { Card, CardBody } from '../../card'
import ContactForm from '../../functional/contact-form'
import { IFormValues, ContactFormVariant } from '../../functional/contact-form/types'
import WaveContainer from '../../wave-container'
import styles from './prettyContactForm.module.scss'
import Checklist from '../../checklist'
import Quotes from '../../../sections/quotes'
import Price from '../../price'
import { commonKeyValues } from '../../../lib/utils'
import Reassurance from './components/reassurance'

const messages = defineMessages({
  heading: {
    id: 'component.design.pretty-contact-form.heading-2',
    defaultMessage: 'Kontakta oss, vi ♥️ bokföring',
  },
  lede: {
    id: 'component.design.pretty-contact-form.lede',
    defaultMessage:
      'Kontakta oss gärna om du är nyfiken på att bli kund, vill förstå hur vi på Dooer kan hjälpa ditt företag eller undrar vad Dooer skulle kosta. Vi återkommer blixtsnabbt.',
  },
  arrowText: {
    id: 'component.design.pretty-contact-form.arrow-text',
    defaultMessage: '…do it, fixa din bokföring en gång för alla',
  },

  checklistItem1: {
    id: 'component.design.pretty-contact-form.side.checklist.item-1',
    defaultMessage: 'Ingen uppsägningstid',
  },
  checklistItem2: {
    id: 'component.design.pretty-contact-form.side.checklist.item-2',
    defaultMessage: '90 dagars nöjd-kund garanti',
  },
  checklistItem3: {
    id: 'component.design.pretty-contact-form.side.checklist.item-3',
    defaultMessage: 'Vi hjälper dig igång',
  },
  tabHeading: {
    id: 'component.design.pretty-contact-form.tabs.heading',
    defaultMessage: 'Mitt företag är:',
  },
  tab1: {
    id: 'component.design.pretty-contact-form.tabs.tab-1',
    defaultMessage: 'Nystartat',
  },
  tab2: {
    id: 'component.design.pretty-contact-form.tabs.tab-2',
    defaultMessage: 'Etablerat',
  },
  tabDescription: {
    id: 'component.design.pretty-contact-form.tabs.description',
    defaultMessage: 'Är du i ditt första räkenskapsår så ser vi företaget som nystartat.',
  },
})

const PrettyContactForm = () => {
  const initialVariant = querystring.parse(window.location.search.replace(/^\?/, '')).variant
  const isContactFormVariant = (value: unknown): value is ContactFormVariant => {
    return (
      typeof value === 'string' && (value === 'companyOld' || value === 'companyRecentlyStartd' || value === 'regular')
    )
  }

  const [variant, setVariant] = React.useState<ContactFormVariant>(
    isContactFormVariant(initialVariant) ? initialVariant : 'companyRecentlyStarted'
  )

  const {
    companyBranch,
    companyStatus,
    companyForm,
    companyEmployees,
    name,
    email,
    phone,
    agreements,
    description,
    companyName,
    companyRevenue,
    bank,
    ...rest
  } = querystring.parse(window.location.search.replace(/^\?/, ''))

  const initialValues: IFormValues = {
    companyBranch: typeof companyBranch === 'string' ? companyBranch : '',
    companyStatus: typeof companyStatus === 'string' ? companyStatus : '',
    companyForm: typeof companyForm === 'string' ? companyForm : '',
    companyEmployees: companyEmployees ? Number(companyEmployees) : 0,
    companyRevenue: typeof companyRevenue === 'string' ? companyRevenue : '',
    name: typeof name === 'string' ? name : '',
    bank: typeof bank === 'string' ? bank : '',
    email: typeof email === 'string' ? email : '',
    phone: typeof phone === 'string' ? phone : '',
    companyName: typeof companyName === 'string' ? companyName : '',
    description: typeof description === 'string' ? description : '',
    agreements: typeof agreements === 'boolean' ? agreements : false,
  }

  return (
    <div className={styles.root} {...rest}>
      <Card className={styles.main}>
        <div className={styles.header}>
          <FormattedMessage tagName="h3" {...messages.heading} />
          <FormattedMessage tagName="p" {...messages.lede} />
        </div>
        <WaveContainer className={styles.wave}>
          <CardBody>
            {variant !== 'regular' && (
              <>
                <h4 className={styles.tabsHeading}>
                  <FormattedMessage {...messages.tabHeading} />
                </h4>
                <div className={styles.tabs}>
                  <div
                    className={cn(styles.tab, variant === 'companyRecentlyStarted' && styles.tabActive)}
                    onClick={() => setVariant('companyRecentlyStarted')}
                    onKeyDown={(e) => e.keyCode === commonKeyValues.enter && setVariant('companyRecentlyStarted')}
                    role="button"
                    tabIndex={0}
                  >
                    <FormattedMessage {...messages.tab1} tagName="span" />
                  </div>
                  <div
                    className={cn(styles.tab, variant === 'companyOld' && styles.tabActive)}
                    onClick={() => setVariant('companyOld')}
                    onKeyDown={(e) => e.keyCode === commonKeyValues.enter && setVariant('companyOld')}
                    role="button"
                    tabIndex={0}
                  >
                    <FormattedMessage {...messages.tab2} tagName="span" />
                  </div>
                </div>
                <h5 className={styles.dimDescription}>
                  <FormattedMessage {...messages.tabDescription} />
                </h5>
                <br />
              </>
            )}

            <Reassurance variant={variant} />

            <ContactForm initialValues={initialValues} variant={variant} />
            <div className={styles.arrowAndText}>
              <div className={styles.arrow} />
              <FormattedMessage tagName="p" {...messages.arrowText} />
            </div>
          </CardBody>
        </WaveContainer>
      </Card>
      {variant === 'regular' && (
        <Card className={styles.side}>
          <CardBody className={styles.sideBody}>
            <div className={styles.summary}>
              <Price amount={895} className={styles.price} prefix="starting-at" variant="normal" />
            </div>

            <hr />

            <Checklist
              center
              handwriting
              items={[
                <FormattedMessage key={1} {...messages.checklistItem1} />,
                <FormattedMessage key={2} {...messages.checklistItem2} />,
                <FormattedMessage key={3} {...messages.checklistItem3} />,
              ]}
              tight
            />

            <hr />

            <Quotes variant="extraSmall" />
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default PrettyContactForm
